import React, { FC, useState } from 'react';
import { Button, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import { Descriptions, DescriptionsRow } from 'common/components';
import { copyText } from 'common/helpers';
import { AuthHooks } from 'common/hooks';
import { ArrowRightShort, Copy, Edit } from 'common/svg';
import { IGood, IUpdateGoodPayload } from 'entities/Goods/Goods.models';
import { ChangeBuyerCodeModal } from 'entities/Goods/components';

interface IComponentProps {
  good: IGood | null;
  loading: boolean;
  onNavigateToProperties: (value: string) => void;
  updateGood: (payload: IUpdateGoodPayload) => Promise<void>;
}

export const GoodGeneralInformation: FC<IComponentProps> = ({ good, loading, onNavigateToProperties, updateGood }) => {
  const [openChangeBuyerCodeModal, setOpenChangeBuyerCodeModal] = useState<boolean>(false);
  const navigate = useNavigate();
  const { isSeller } = AuthHooks.useIsSeller();

  const generalProperties = good?.properties.slice(0, 6);

  const copySellerCode = (): void => {
    if (good?.sellerCode) {
      copyText(good.sellerCode);
    }
  };

  const copyBuyerCode = (): void => {
    if (good?.buyerCode) {
      copyText(good.buyerCode);
    }
  };

  const handleOpenChangeBuyerCodeModal = (): void => {
    setOpenChangeBuyerCodeModal(true);
  };

  const closeChangeBuyerCodeModal = (): void => {
    setOpenChangeBuyerCodeModal(false);
  };

  const changeBuyerCode = ({ buyerCode, onSuccess }: Omit<IUpdateGoodPayload, 'id'>): void => {
    if (!good) {
      return;
    }

    updateGood({
      id: good.id,
      buyerCode: buyerCode?.length ? buyerCode : null,
      onSuccess,
    });
  };

  const navigateToCategory = (catalogId: number, categoryId: number): void => {
    navigate(`/catalog/${catalogId}/${categoryId}`);
  };

  const navigateToProperties = (): void => {
    onNavigateToProperties('properties');
  };

  return (
    <div className="good-general-information">
      <div className="mb-6 ff-medium fs-xl">{good?.name}</div>

      <div className="mb-6 flex align-center gap-6">
        {good?.sellerCode && (
          <Tooltip
            placement="bottom"
            arrow={false}
            overlayInnerStyle={{ padding: 12, borderRadius: 12 }}
            title={
              <div className="flex align-center gap-3">
                {good.sellerCode}
                <Copy className="icon-dark-grey cursor-pointer" onClick={copySellerCode} />
              </div>
            }
          >
            <span className="ff-regular fs-s color-dark-grey" style={{ lineHeight: '28px' }}>
              {good.sellerCode}
            </span>
          </Tooltip>
        )}

        {!isSeller && good && (
          <div>
            {good?.buyerCode ? (
              <div className="flex align-center gap-3">
                <Tooltip
                  placement="bottom"
                  arrow={false}
                  overlayInnerStyle={{ padding: 12, borderRadius: 12 }}
                  title={
                    <div className="flex align-center gap-3">
                      {good.buyerCode}
                      <Copy className="icon-dark-grey cursor-pointer" onClick={copyBuyerCode} />
                    </div>
                  }
                >
                  <span className="ff-regular fs-s color-bright-green" style={{ lineHeight: '28px' }}>
                    {good.buyerCode}
                  </span>
                </Tooltip>

                <Tooltip
                  placement="bottom"
                  title="Изменить артикул клиента"
                  overlayInnerStyle={{ padding: 12, borderRadius: 12 }}
                >
                  <Edit className="icon-dark-grey cursor-pointer" onClick={handleOpenChangeBuyerCodeModal} />
                </Tooltip>
              </div>
            ) : (
              <Button className="button-text dark-green" onClick={handleOpenChangeBuyerCodeModal}>
                Добавить артикул
                <Edit className="icon-dark-green" />
              </Button>
            )}
          </div>
        )}
      </div>

      <div>
        <div className="mb-6 flex align-center justify-between">
          <span className="ff-medium fs-m">Основые характеристики</span>

          <Button className="button-text dark-green" onClick={navigateToProperties}>
            <span>Все характеристики</span>
            <ArrowRightShort className="icon-dark-green" />
          </Button>
        </div>

        <div className="mb-9">
          <Descriptions>
            {generalProperties?.map((item) => {
              let value = item.value;

              if (item.unitOfMeasurement) {
                value += ` ${item.unitOfMeasurement}`;
              }

              return <DescriptionsRow key={item.propertyId} label={item.propertyName} value={value} />;
            })}
          </Descriptions>
        </div>

        <div className="flex align-center wrap gap-4">
          {good?.categories?.map((item) => {
            return (
              <div
                key={item.categoryId}
                className="ff-regular fs-s good-general-information-category"
                onClick={() => navigateToCategory(item.catalogId, item.categoryId)}
                style={{
                  pointerEvents: !item.catalogId ? 'none' : 'auto',
                  cursor: !item.catalogId ? 'default' : 'pointer',
                }}
              >
                {item.categoryName}
              </div>
            );
          })}
        </div>
      </div>

      <ChangeBuyerCodeModal
        open={openChangeBuyerCodeModal}
        loading={loading}
        code={good?.buyerCode}
        onCancel={closeChangeBuyerCodeModal}
        onSubmit={changeBuyerCode}
      />
    </div>
  );
};
